// MapNodeHeaderFields.jsx — ticket 04 (map-node-ui-audit): MUI-styled header controls for the
// pm-map-node embed chrome. Rendered into a React root that MapNodeChrome (a vanilla
// ProseMirror NodeView chrome class) owns and re-renders imperatively — see that file's
// `_renderHeader`/`destroy` for the createRoot lifecycle.
//
// All three interactive fields use full MUI JS-driven components (Select + X DatePicker) rather
// than MUI's native mode. The previous native-mode approach (`SelectProps={{native:true}}` /
// `TextField type="date"`) coupled React's controlled-input reconciliation to the browser's own
// value tracking, causing the native `change` event to never fire on the DTG input when the
// header re-rendered for unrelated reasons (view/basemap changes triggering a React commit that
// snapped the input value back before blur). Switching to fully React-owned MUI components
// removes the native-DOM value layer entirely — React manages value/state end-to-end, and
// onChange fires through normal React synthetic events, eliminating this entire bug class.
//
// Shape parity: the DTG field intentionally uses MUI X's *default* field (sectioned
// day/month/year text + a calendar icon button), not a custom button-trigger slot (unlike
// MapTimeline.jsx's DtgTriggerButton) — the default field renders as the same OutlinedInput box
// with a floating label as the View/Basemap `Select`s, so all three controls share one visual
// shape/height in this row. Click the calendar icon (`pm-map-node-dtg-trigger`) to open the
// popup, or type directly into a date section.
//
// Popup portaling: MUI's Select menu and DatePicker calendar render via a Portal to
// document.body (required since `.pm-map-node { overflow:hidden }` would clip them if rendered
// inline). MapNodeChrome.containsTarget() accounts for these portaled elements via the
// `viewPopperRef`/`dtgPopperRef`/`basemapPopperRef` callback refs below that capture each
// popup's root DOM node while open.

import React from 'react';
import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { SitrepButton } from './SitrepButton';

const FIELD_SX = { flexShrink: 1, minWidth: 0 };

export function MapNodeHeaderFields({
    viewOptions,
    viewValue,
    onViewChange,
    onViewOpen,
    onViewClose,
    viewPopperRef,
    dtgValue,
    onDtgChange,
    onDtgOpen,
    onDtgClose,
    dtgPopperRef,
    basemapOptions,
    basemapValue,
    onBasemapChange,
    onBasemapOpen,
    onBasemapClose,
    basemapPopperRef,
    editBtnRef,
    onEditClick,
    onGenerateSitrep,
    sitrepDialogPopperRef,
    sitrepDisabled,
}) {
    const dtgDayjs = dtgValue ? dayjs.utc(dtgValue) : null;

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{ minWidth: 0, flex: '1 1 auto' }}
            >
                {/* View select — full MUI Select (non-native), MenuProps.PaperProps.ref captures
                    the menu Paper's DOM node while open for MapNodeChrome.containsTarget(). */}
                <FormControl
                    size="small"
                    variant="outlined"
                    sx={{ ...FIELD_SX, maxWidth: 150 }}
                    data-testid="pm-map-node-view-select"
                >
                    <InputLabel id="pm-map-node-view-label">View</InputLabel>
                    <Select
                        labelId="pm-map-node-view-label"
                        label="View"
                        value={viewValue}
                        onChange={(e) => onViewChange(e.target.value)}
                        onOpen={onViewOpen}
                        onClose={onViewClose}
                        inputProps={{ title: 'Select view', 'aria-label': 'Select map view' }}
                        sx={{ overflow: 'hidden' }}
                        MenuProps={{
                            PaperProps: { ref: viewPopperRef },
                        }}
                    >
                        {viewOptions.map((o) => (
                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* DTG date — MUI X DatePicker using its *default* field (sectioned
                    day/month/year text + calendar icon button), so it renders as the same
                    OutlinedInput box + floating label as the View/Basemap Selects above (shape
                    parity across the row). Wraps with a LocalizationProvider (no global provider
                    exists; each consumer owns one, matching MapTimeline.jsx / DateTimeField.jsx's
                    established convention). slotProps.popper.ref captures the calendar Popper's
                    DOM node while open. */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="DTG"
                        value={dtgDayjs}
                        format="YYYY-MM-DD"
                        onChange={(next) => {
                            if (!next || !next.isValid()) {
                                onDtgChange(null);
                                return;
                            }
                            // Convert to UTC-midnight of the picked calendar day, matching
                            // msToDateInputValue's UTC convention and useDtgRange's UTC semantics.
                            onDtgChange(dayjs.utc(next.format('YYYY-MM-DD')).valueOf());
                        }}
                        onOpen={onDtgOpen}
                        onClose={onDtgClose}
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: { ...FIELD_SX, maxWidth: 170 },
                                'data-testid': 'pm-map-node-dtg-select',
                            },
                            openPickerButton: {
                                'data-testid': 'pm-map-node-dtg-trigger',
                                'aria-label': 'Select date-time group (DTG)',
                                title: 'Select DTG',
                            },
                            popper: { ref: dtgPopperRef },
                        }}
                    />
                </LocalizationProvider>

                {/* Basemap select — same pattern as View select above. */}
                <FormControl
                    size="small"
                    variant="outlined"
                    sx={{ ...FIELD_SX, maxWidth: 140 }}
                    data-testid="pm-map-node-basemap-select"
                >
                    <InputLabel id="pm-map-node-basemap-label">Basemap</InputLabel>
                    <Select
                        labelId="pm-map-node-basemap-label"
                        label="Basemap"
                        value={basemapValue}
                        onChange={(e) => onBasemapChange(e.target.value)}
                        onOpen={onBasemapOpen}
                        onClose={onBasemapClose}
                        inputProps={{ title: 'Select basemap', 'aria-label': 'Select basemap style' }}
                        MenuProps={{
                            PaperProps: { ref: basemapPopperRef },
                        }}
                    >
                        {basemapOptions.map((o) => (
                            <MenuItem key={o.url} value={o.url}>{o.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
            <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                <SitrepButton
                    onGenerateSitrep={onGenerateSitrep}
                    dialogPaperRef={sitrepDialogPopperRef}
                    disabled={sitrepDisabled}
                />
                <Button
                    ref={editBtnRef}
                    variant="text"
                    size="small"
                    sx={{ flexShrink: 0 }}
                    aria-label="Edit map"
                    onClick={onEditClick}
                >
                    Edit
                </Button>
            </Stack>
        </>
    );
}
